import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Package, Plus, Trash2, Upload, X, Search, ScanLine, Copy, ArrowLeft, ImagePlus, Loader2, FileDown, CheckCircle2, RotateCcw, PackageCheck, Share2, Maximize2 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ImageLightbox } from '@/components/ImageLightbox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const resolveUrl = (url) => (url && url.startsWith('/') ? `${BACKEND_URL}${url}` : url);

// Server-side downscaled + cached thumbnail — keeps the grid fast
const thumbUrl = (url, w = 400) => {
  const full = resolveUrl(url);
  if (!full) return full;
  return full.startsWith(BACKEND_URL) ? `${full}?w=${w}` : full;
};

// Upload files with limited concurrency (much faster than one-by-one)
const uploadPool = async (files, worker, concurrency = 4, onProgress) => {
  let cursor = 0;
  let done = 0;
  const runners = Array.from({ length: Math.min(concurrency, files.length) }, async () => {
    while (cursor < files.length) {
      const i = cursor++;
      await worker(files[i]);
      done += 1;
      if (onProgress) onProgress(done, files.length);
    }
  });
  await Promise.all(runners);
};

const shareOnWhatsApp = (item) => {
  if (!item) return;
  const lines = [
    '*Bandhani Heritage*',
    `_${item.name}_`,
    `Category: ${item.category}`,
    `Price: ₹${(item.price || 0).toLocaleString('en-IN')}`,
    `Stock: ${item.stock || (item.images?.length || 0)} unique piece(s)`,
  ];
  if (item.notes) lines.push('', item.notes);
  const urls = (item.images || []).map((img) => resolveUrl(img.url));
  if (urls.length > 0) {
    lines.push('', '📸 Photos:');
    urls.forEach((u) => lines.push(u));
  }
  const msg = encodeURIComponent(lines.join('\n'));
  window.open(`https://wa.me/?text=${msg}`, '_blank', 'noopener,noreferrer');
};

const AdminInventory = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [soldItems, setSoldItems] = useState([]);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterPriceMin, setFilterPriceMin] = useState('');
  const [filterPriceMax, setFilterPriceMax] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [duplicatesOpen, setDuplicatesOpen] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [catalogueOpen, setCatalogueOpen] = useState(false);
  const [markSoldOpen, setMarkSoldOpen] = useState(false);

  useEffect(() => {
    if (loading) return;
    const canAccess = user && (user.is_admin || user.role === 'inventory_member');
    if (!canAccess) {
      navigate('/');
      return;
    }
    fetchCategories();
    fetchItems();
    fetchSoldItems();
  }, [user, loading]);

  useEffect(() => {
    const canAccess = user && (user.is_admin || user.role === 'inventory_member');
    if (canAccess) fetchItems();
  }, [filterCategory, filterPriceMin, filterPriceMax, searchQuery]);

  const fetchCategories = async () => {
    try {
      const res = await api.get('/inventory/categories');
      setCategories(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchItems = async () => {
    try {
      const params = { status: 'available' };
      if (filterCategory && filterCategory !== 'all') params.category = filterCategory;
      if (filterPriceMin) params.price_min = filterPriceMin;
      if (filterPriceMax) params.price_max = filterPriceMax;
      if (searchQuery) params.q = searchQuery;
      const res = await api.get('/inventory/items', { params });
      setItems(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchSoldItems = async () => {
    try {
      const res = await api.get('/inventory/items', { params: { status: 'sold' } });
      setSoldItems(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const refreshAll = () => { fetchItems(); fetchSoldItems(); };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!newCategory.trim()) return;
    try {
      await api.post('/inventory/categories', { name: newCategory.trim() });
      toast.success('Category added');
      setNewCategory('');
      fetchCategories();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add category');
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      await api.delete(`/inventory/categories/${id}`);
      toast.success('Category deleted');
      fetchCategories();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleDeleteItem = async (id) => {
    if (!window.confirm('Delete this item and all its images?')) return;
    try {
      await api.delete(`/inventory/items/${id}`);
      toast.success('Item deleted');
      fetchItems();
    } catch {
      toast.error('Failed to delete item');
    }
  };

  const openDuplicates = async () => {
    try {
      const res = await api.get('/inventory/duplicates');
      setDuplicates(res.data);
      setDuplicatesOpen(true);
    } catch {
      toast.error('Failed to scan duplicates');
    }
  };

  return (
    <div className="py-24 md:py-32 max-w-[1400px] mx-auto px-4 md:px-8" data-testid="admin-inventory-page">
      <div className="flex flex-wrap items-center gap-4 mb-8">
        <Button variant="ghost" size="icon" onClick={() => navigate(user?.is_admin ? '/admin' : '/')} data-testid="back-to-admin">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-[240px]">
          <h1 className="text-4xl md:text-5xl font-light tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Inventory Management
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Private wholesale inventory · separate from the public storefront</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" className="rounded-full" onClick={() => setCatalogueOpen(true)} data-testid="catalogue-btn">
            <FileDown className="h-4 w-4 mr-2" /> Catalogue PDF
          </Button>
          <Button variant="outline" className="rounded-full" onClick={() => setMarkSoldOpen(true)} data-testid="mark-sold-btn">
            <CheckCircle2 className="h-4 w-4 mr-2" /> Mark Sold (Scan)
          </Button>
          <Button variant="outline" className="rounded-full" onClick={openDuplicates} data-testid="find-duplicates-btn">
            <Copy className="h-4 w-4 mr-2" /> Find Duplicates
          </Button>
        </div>
      </div>

      <Tabs defaultValue="items" className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="items" data-testid="tab-items">Available ({items.length})</TabsTrigger>
          <TabsTrigger value="sold" data-testid="tab-sold">Sold ({soldItems.length})</TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories">Categories ({categories.length})</TabsTrigger>
        </TabsList>

        {/* ITEMS */}
        <TabsContent value="items">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-6 p-4 bg-secondary/40 rounded-xl">
            <div>
              <Label className="text-xs uppercase tracking-widest">Search</Label>
              <div className="relative mt-1">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Name / notes"
                  className="rounded-full pl-9"
                  data-testid="filter-search"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Category</Label>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="rounded-full mt-1" data-testid="filter-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((c) => (
                    <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Price Min</Label>
              <Input
                type="number"
                value={filterPriceMin}
                onChange={(e) => setFilterPriceMin(e.target.value)}
                placeholder="₹0"
                className="rounded-full mt-1"
                data-testid="filter-price-min"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Price Max</Label>
              <Input
                type="number"
                value={filterPriceMax}
                onChange={(e) => setFilterPriceMax(e.target.value)}
                placeholder="₹∞"
                className="rounded-full mt-1"
                data-testid="filter-price-max"
              />
            </div>
            <div className="flex items-end">
              <Button
                className="rounded-full w-full"
                onClick={() => setAddItemOpen(true)}
                disabled={categories.length === 0}
                data-testid="add-item-btn"
              >
                <Plus className="h-4 w-4 mr-2" /> Add Item
              </Button>
            </div>
          </div>

          {categories.length === 0 && (
            <div className="border border-dashed border-border rounded-xl p-8 text-center mb-6">
              <p className="text-sm text-muted-foreground">Create a category first to start adding inventory items.</p>
            </div>
          )}

          {items.length === 0 ? (
            <div className="border border-border rounded-xl p-12 text-center">
              <Package className="h-8 w-8 mx-auto mb-3 text-muted-foreground/50" />
              <p className="text-sm text-muted-foreground">No inventory items yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="inventory-items-list">
              {items.map((item) => (
                <InventoryItemCard
                  key={item.id}
                  item={item}
                  categories={categories}
                  onDelete={() => handleDeleteItem(item.id)}
                  onRefresh={fetchItems}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* SOLD ITEMS */}
        <TabsContent value="sold" data-testid="sold-tab-content">
          {soldItems.length === 0 ? (
            <div className="border border-border rounded-xl p-12 text-center">
              <PackageCheck className="h-8 w-8 mx-auto mb-3 text-muted-foreground/50" />
              <p className="text-sm text-muted-foreground">No sold items yet.</p>
              <p className="text-xs text-muted-foreground mt-1">
                Use <span className="font-medium">Mark Sold (Scan)</span> to bulk-move photos into this section.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="sold-items-list">
              {soldItems.map((item) => (
                <SoldItemCard key={item.id} item={item} onRefresh={refreshAll} />
              ))}
            </div>
          )}
        </TabsContent>

        {/* CATEGORIES */}
        <TabsContent value="categories">
          <form onSubmit={handleAddCategory} className="flex gap-2 mb-6 max-w-md" data-testid="add-category-form">
            <Input
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              placeholder="e.g. Bandhani Sarees, Gharchola, Dupattas"
              className="rounded-full"
              data-testid="new-category-input"
            />
            <Button type="submit" className="rounded-full" data-testid="add-category-btn">
              <Plus className="h-4 w-4 mr-1" /> Add
            </Button>
          </form>
          {categories.length === 0 ? (
            <p className="text-sm text-muted-foreground">No categories yet.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {categories.map((c) => (
                <div key={c.id} className="border border-border rounded-xl p-4 flex justify-between items-center" data-testid={`category-row-${c.id}`}>
                  <div>
                    <p className="font-medium">{c.name}</p>
                    <p className="text-xs text-muted-foreground">{items.filter((i) => i.category === c.name).length} items</p>
                  </div>
                  {user?.is_admin && (
                    <Button variant="ghost" size="icon" onClick={() => handleDeleteCategory(c.id)} data-testid={`delete-category-${c.id}`}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Add Item Dialog */}
      <AddItemDialog
        open={addItemOpen}
        onOpenChange={setAddItemOpen}
        categories={categories}
        onCreated={() => { setAddItemOpen(false); fetchItems(); }}
      />

      {/* Duplicates Dialog */}
      <Dialog open={duplicatesOpen} onOpenChange={setDuplicatesOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="duplicates-dialog">
          <DialogHeader>
            <DialogTitle>Duplicate Photos</DialogTitle>
          </DialogHeader>
          {duplicates.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">No duplicate images found across inventory.</p>
          ) : (
            <div className="space-y-4">
              {duplicates.map((g) => (
                <div key={g.hash} className="border border-border rounded-lg p-3">
                  <p className="text-xs font-mono text-muted-foreground mb-2">Hash: {g.hash.slice(0, 12)}… · {g.count} occurrences</p>
                  <div className="grid grid-cols-2 gap-2">
                    {g.occurrences.map((o, i) => (
                      <div key={i} className="flex items-center gap-2 bg-secondary/40 p-2 rounded">
                        <img src={thumbUrl(o.url, 96)} alt="" loading="lazy" className="w-12 h-12 object-cover rounded" />
                        <div className="text-xs">
                          <p className="font-medium">{o.item_name}</p>
                          <p className="text-muted-foreground">{o.category}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Mark Sold Dialog (multi-photo) */}
      <MarkSoldDialog
        open={markSoldOpen}
        onOpenChange={setMarkSoldOpen}
        onDone={refreshAll}
      />

      {/* Catalogue PDF Dialog */}
      <CatalogueDialog
        open={catalogueOpen}
        onOpenChange={setCatalogueOpen}
        categories={categories}
      />
    </div>
  );
};

// =============== ADD ITEM DIALOG ===============
const AddItemDialog = ({ open, onOpenChange, categories, onCreated }) => {
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [price, setPrice] = useState('');
  const [notes, setNotes] = useState('');
  const [images, setImages] = useState([]); // {url, path, hash}
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setName(''); setCategory(''); setPrice(''); setNotes(''); setImages([]);
    }
  }, [open]);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    const pics = files.filter((f) => f.type.startsWith('image/'));
    if (pics.length === 0) return;
    setUploading(true);
    setProgress({ done: 0, total: pics.length });
    const seenHashes = new Set(images.map((i) => i.hash));
    const uploaded = [];
    let skipped = 0;
    await uploadPool(pics, async (file) => {
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await api.post('/inventory/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        const img = { url: res.data.url, path: res.data.path, hash: res.data.hash };
        if (seenHashes.has(img.hash)) { skipped += 1; return; }
        seenHashes.add(img.hash);
        uploaded.push(img);
      } catch (err) {
        toast.error(`Upload failed: ${file.name}`);
      }
    }, 4, (done, total) => setProgress({ done, total }));
    setImages((prev) => [...prev, ...uploaded]);
    setUploading(false);
    setProgress(null);
    if (skipped > 0) toast.warning(`${skipped} duplicate photo(s) skipped`);
  };

  const removeImage = (hash) => setImages(images.filter((i) => i.hash !== hash));

  const submit = async () => {
    if (!name.trim() || !category || !price) { toast.error('Name, category and price are required'); return; }
    if (images.length === 0) { toast.error('Add at least one image (stock = number of images)'); return; }
    try {
      await api.post('/inventory/items', {
        name: name.trim(),
        category,
        price: parseFloat(price),
        notes: notes.trim() || null,
        images
      });
      toast.success('Item added');
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add item');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="add-item-dialog">
        <DialogHeader>
          <DialogTitle>Add Inventory Item</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest">Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-full mt-1" data-testid="item-name-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="rounded-full mt-1" data-testid="item-category-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (<SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Price (₹)</Label>
            <Input type="number" value={price} onChange={(e) => setPrice(e.target.value)} className="rounded-full mt-1" data-testid="item-price-input" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Notes (optional)</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="rounded-xl mt-1" data-testid="item-notes-input" />
          </div>

          {/* Multi image upload */}
          <div>
            <Label className="text-xs uppercase tracking-widest">Photos · Each photo = 1 unique piece (stock auto-count)</Label>
            <div className="mt-2">
              <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleFiles(Array.from(e.target.files))} data-testid="item-images-input" />
              <Button type="button" variant="outline" className="rounded-full w-full" disabled={uploading} onClick={() => fileRef.current?.click()}>
                {uploading ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading {progress ? `${progress.done}/${progress.total}` : ''}…</>) : (<><ImagePlus className="h-4 w-4 mr-2" /> Upload multiple photos</>)}
              </Button>
            </div>
            {images.length > 0 && (
              <div className="mt-3">
                <p className="text-xs mb-2">Stock: <span className="font-semibold">{images.length}</span></p>
                <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
                  {images.map((img) => (
                    <div key={img.hash} className="relative aspect-square">
                      <img src={thumbUrl(img.url, 200)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover rounded-lg border border-border" />
                      <button type="button" onClick={() => removeImage(img.hash)} className="absolute -top-1 -right-1 bg-destructive text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="rounded-full" onClick={submit} disabled={uploading} data-testid="submit-item-btn">Save Item</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// =============== ITEM CARD (with inline image add / remove) ===============
const InventoryItemCard = ({ item, categories, onDelete, onRefresh }) => {
  const [expanded, setExpanded] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });
  const fileRef = useRef(null);

  const photoUrls = (item.images || []).map((img) => resolveUrl(img.url));

  const handleAddImage = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    setProgress({ done: 0, total: files.length });
    let dupes = 0;
    await uploadPool(files, async (file) => {
      try {
        const fd = new FormData();
        fd.append('file', file);
        await api.post(`/inventory/items/${item.id}/images`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      } catch (err) {
        if (err.response?.status === 409) dupes += 1;
        else toast.error('Upload failed');
      }
    }, 4, (done, total) => setProgress({ done, total }));
    setUploading(false);
    setProgress(null);
    if (e.target) e.target.value = '';
    if (dupes > 0) toast.warning(`${dupes} duplicate photo(s) skipped`);
    onRefresh();
  };

  const handleRemoveImage = async (hash) => {
    try {
      await api.delete(`/inventory/items/${item.id}/images`, { params: { hash } });
      onRefresh();
    } catch {
      toast.error('Failed to remove image');
    }
  };

  return (
    <div className="border border-border rounded-xl overflow-hidden" data-testid={`inventory-item-${item.id}`}>
      <button
        type="button"
        className="relative aspect-[4/5] bg-secondary w-full block group"
        onClick={() => photoUrls.length > 0 && setLightbox({ open: true, index: 0 })}
        data-testid={`open-gallery-${item.id}`}
      >
        {item.images?.[0] ? (
          <img
            src={thumbUrl(item.images[0].url, 500)}
            alt={item.name}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          />
        ) : (
          <div className="flex items-center justify-center h-full text-muted-foreground"><Package /></div>
        )}
        <span className="absolute top-2 right-2 bg-primary text-primary-foreground text-xs px-2 py-1 rounded-full">
          Stock: {item.stock}
        </span>
        {photoUrls.length > 0 && (
          <span className="absolute bottom-2 left-2 flex items-center gap-1 bg-black/60 text-white text-[10px] uppercase tracking-widest px-2 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
            <Maximize2 className="h-3 w-3" /> View large
          </span>
        )}
        {item.sold_count > 0 && (
          <span className="absolute top-2 left-2 bg-emerald-600 text-white text-[10px] uppercase tracking-widest px-2 py-1 rounded-full">
            {item.sold_count} sold
          </span>
        )}
      </button>
      <div className="p-4 space-y-2">
        <div className="flex justify-between items-start">
          <div className="min-w-0">
            <p className="font-medium truncate">{item.name}</p>
            <p className="text-xs text-muted-foreground">{item.category}</p>
          </div>
          <p className="text-lg font-light">₹{item.price?.toLocaleString('en-IN')}</p>
        </div>
        {item.notes && <p className="text-xs text-muted-foreground line-clamp-2">{item.notes}</p>}
        <div className="flex gap-2 pt-2">
          <Button size="sm" variant="outline" className="rounded-full flex-1" onClick={() => setExpanded(!expanded)} data-testid={`toggle-photos-${item.id}`}>
            {expanded ? 'Hide' : `Manage ${item.stock} photos`}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="rounded-full text-green-700 border-green-200 hover:bg-green-50"
            onClick={() => shareOnWhatsApp(item)}
            title="Share all photos on WhatsApp"
            data-testid={`share-whatsapp-${item.id}`}
          >
            <Share2 className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="destructive" className="rounded-full" onClick={onDelete} data-testid={`delete-item-${item.id}`}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
        {expanded && (
          <div className="pt-3 border-t border-border">
            <div className="grid grid-cols-3 gap-2 mb-3">
              {(item.images || []).map((img, i) => (
                <div key={img.hash} className="relative aspect-square group">
                  <button
                    type="button"
                    className="w-full h-full block"
                    onClick={() => setLightbox({ open: true, index: i })}
                    data-testid={`open-photo-${item.id}-${i}`}
                  >
                    <img src={thumbUrl(img.url, 240)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover rounded" />
                  </button>
                  <button onClick={() => handleRemoveImage(img.hash)} className="absolute -top-1 -right-1 bg-destructive text-white rounded-full w-5 h-5 flex items-center justify-center text-xs" data-testid={`remove-photo-${item.id}-${i}`}>
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={handleAddImage} />
            <Button
              size="sm" variant="outline" className="rounded-full w-full"
              disabled={uploading}
              onClick={() => fileRef.current?.click()}
              data-testid={`add-image-${item.id}`}
            >
              {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
              {uploading && progress ? `Uploading ${progress.done}/${progress.total}…` : 'Add More Photos'}
            </Button>
          </div>
        )}
      </div>

      <ImageLightbox
        open={lightbox.open}
        images={photoUrls}
        startIndex={lightbox.index}
        title={`${item.name} · ₹${item.price?.toLocaleString('en-IN')}`}
        onClose={() => setLightbox({ open: false, index: 0 })}
      />
    </div>
  );
};

// =============== SOLD ITEM CARD (sold pieces gallery + restore) ===============
const SoldItemCard = ({ item, onRefresh }) => {
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });
  const soldPhotos = item.sold_images || [];
  const urls = soldPhotos.map((img) => resolveUrl(img.url));
  const fullySold = item.status === 'sold';

  const restore = async (hash) => {
    try {
      await api.put(`/inventory/items/${item.id}/restore`, null, { params: hash ? { hash } : {} });
      toast.success(hash ? 'Piece restored to stock' : 'All pieces restored to stock');
      onRefresh();
    } catch {
      toast.error('Failed to restore');
    }
  };

  return (
    <div className="border border-border rounded-xl overflow-hidden" data-testid={`sold-item-${item.id}`}>
      <button
        type="button"
        className="relative aspect-[4/5] bg-secondary w-full block group"
        onClick={() => urls.length > 0 && setLightbox({ open: true, index: 0 })}
        data-testid={`open-sold-gallery-${item.id}`}
      >
        {soldPhotos[0] ? (
          <img src={thumbUrl(soldPhotos[0].url, 500)} alt={item.name} loading="lazy" decoding="async" className="w-full h-full object-cover grayscale" />
        ) : (
          <div className="flex items-center justify-center h-full text-muted-foreground"><Package /></div>
        )}
        <span className="absolute top-2 right-2 bg-emerald-600 text-white text-xs px-3 py-1 rounded-full uppercase tracking-widest">
          {fullySold ? 'Sold Out' : `${item.sold_count} Sold`}
        </span>
        {!fullySold && (
          <span className="absolute top-2 left-2 bg-primary text-primary-foreground text-[10px] uppercase tracking-widest px-2 py-1 rounded-full">
            {item.stock} in stock
          </span>
        )}
      </button>
      <div className="p-4 space-y-2">
        <div className="flex justify-between items-start">
          <div className="min-w-0">
            <p className="font-medium truncate">{item.name}</p>
            <p className="text-xs text-muted-foreground">{item.category}</p>
          </div>
          <p className="text-lg font-light">₹{item.price?.toLocaleString('en-IN')}</p>
        </div>
        {soldPhotos.length > 0 && (
          <div className="grid grid-cols-4 gap-2 pt-1">
            {soldPhotos.map((img, i) => (
              <div key={img.hash} className="relative aspect-square group">
                <button type="button" className="w-full h-full block" onClick={() => setLightbox({ open: true, index: i })} data-testid={`open-sold-photo-${item.id}-${i}`}>
                  <img src={thumbUrl(img.url, 160)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover rounded grayscale" />
                </button>
                <button
                  onClick={() => restore(img.hash)}
                  title="Restore this piece to stock"
                  className="absolute -top-1 -right-1 bg-secondary border border-border rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`restore-piece-${item.id}-${i}`}
                >
                  <RotateCcw className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        {item.sold_at && (
          <p className="text-xs text-muted-foreground">
            Last sold {new Date(item.sold_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
          </p>
        )}
        <Button size="sm" variant="outline" className="rounded-full w-full mt-2"
          onClick={() => restore(null)}
          data-testid={`restore-${item.id}`}
        >
          <RotateCcw className="h-3.5 w-3.5 mr-1" /> Restore all sold pieces
        </Button>
      </div>

      <ImageLightbox
        open={lightbox.open}
        images={urls}
        startIndex={lightbox.index}
        title={`${item.name} · sold pieces`}
        onClose={() => setLightbox({ open: false, index: 0 })}
      />
    </div>
  );
};

// =============== MARK SOLD DIALOG (multi-photo → mark items sold) ===============
const MarkSoldDialog = ({ open, onOpenChange, onDone }) => {
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) setResult(null);
  }, [open]);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append('files', f));
      const res = await api.post('/inventory/mark-sold-by-photos', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(res.data);
      const sold = res.data.photos_sold ?? res.data.marked_sold;
      const noMatch = res.data.no_match;
      if (sold > 0) {
        const extra = res.data.items_fully_sold > 0 ? ` · ${res.data.items_fully_sold} item(s) now sold out` : '';
        toast.success(`${sold} piece(s) removed from stock${extra}`);
      }
      if (sold === 0 && noMatch > 0) toast.warning('No matching photos found in inventory');
      onDone();
    } catch {
      toast.error('Failed to process photos');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="mark-sold-dialog">
        <DialogHeader>
          <DialogTitle>Mark items as Sold by Photo</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Upload one or many photos. Each matching photo is the exact piece that was sold — it is removed from that
          item's stock and moved to the <span className="font-medium">Sold</span> section. The item itself only moves
          to Sold once every photo is sold.
        </p>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleFiles}
          data-testid="mark-sold-file-input"
        />
        <Button
          className="rounded-full w-full"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid="mark-sold-upload-btn"
        >
          {uploading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Scanning…</> : <><Upload className="h-4 w-4 mr-2" /> Upload photos</>}
        </Button>
        {result && (
          <div className="mt-4 space-y-3">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2">
                <p className="text-2xl font-light text-emerald-700">{result.photos_sold ?? result.marked_sold}</p>
                <p className="text-[10px] uppercase tracking-widest text-emerald-700">Pieces Sold</p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-2">
                <p className="text-2xl font-light text-amber-700">{result.already_sold}</p>
                <p className="text-[10px] uppercase tracking-widest text-amber-700">Already Sold</p>
              </div>
              <div className="bg-secondary rounded-lg p-2">
                <p className="text-2xl font-light">{result.no_match}</p>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">No Match</p>
              </div>
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {result.details.map((d, i) => (
                <div key={i} className="border border-border rounded-lg p-2 text-xs" data-testid={`mark-sold-row-${i}`}>
                  <p className="font-medium truncate">{d.filename}</p>
                  {d.status === 'no_match' && <p className="text-muted-foreground">No matching item</p>}
                  {d.status === 'too_large' && <p className="text-destructive">File too large (max 10MB)</p>}
                  {d.matched && d.matched.length > 0 && (
                    <ul className="mt-1 space-y-0.5">
                      {d.matched.map((m, j) => (
                        <li key={j} className={m.status === 'already_sold' ? 'text-amber-700' : 'text-emerald-700'}>
                          {m.status === 'already_sold'
                            ? `↻ Already sold: ${m.name}`
                            : `✓ Piece sold: ${m.name}${m.item_fully_sold ? ' — sold out' : ` · ${m.remaining_stock} left`}`}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// =============== CATALOGUE PDF DIALOG ===============
const CatalogueDialog = ({ open, onOpenChange, categories }) => {
  const [category, setCategory] = useState('all');
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [title, setTitle] = useState('');
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!open) {
      setCategory('all'); setPriceMin(''); setPriceMax(''); setTitle('');
    }
  }, [open]);

  const handleDownload = async () => {
    setGenerating(true);
    try {
      const params = {};
      if (category && category !== 'all') params.category = category;
      if (priceMin) params.price_min = priceMin;
      if (priceMax) params.price_max = priceMax;
      if (title.trim()) params.title = title.trim();
      const res = await api.get('/inventory/catalogue.pdf', { params, responseType: 'blob', timeout: 300000 });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const bits = ['bandhani-catalogue'];
      if (category && category !== 'all') bits.push(category.toLowerCase().replace(/\s+/g, '-'));
      a.download = `${bits.join('-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Catalogue downloaded');
      onOpenChange(false);
    } catch (err) {
      const msg = err.response?.status === 404
        ? 'No items match the given filters'
        : err.code === 'ECONNABORTED'
          ? 'Catalogue is taking too long — try filtering by category or a price range'
          : 'Failed to generate PDF';
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="catalogue-dialog">
        <DialogHeader>
          <DialogTitle>Generate Catalogue PDF</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">Filter your inventory and download a ready-to-share PDF for wholesale buyers.</p>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-widest">Title (optional)</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Diwali Collection 2026" className="rounded-full mt-1" data-testid="catalogue-title-input" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="rounded-full mt-1" data-testid="catalogue-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {categories.map((c) => (<SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs uppercase tracking-widest">Price Min</Label>
              <Input type="number" value={priceMin} onChange={(e) => setPriceMin(e.target.value)} placeholder="₹0" className="rounded-full mt-1" data-testid="catalogue-price-min" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Price Max</Label>
              <Input type="number" value={priceMax} onChange={(e) => setPriceMax(e.target.value)} placeholder="₹∞" className="rounded-full mt-1" data-testid="catalogue-price-max" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="rounded-full" onClick={handleDownload} disabled={generating} data-testid="catalogue-download-btn">
            {generating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Building…</> : <><FileDown className="h-4 w-4 mr-2" /> Download PDF</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminInventory;